/**
 * JSON-LD Schema.org markup for rich snippets and EEAT.
 * Organization + LocalBusiness (site-wide), BreadcrumbList (per page), FAQPage (contact).
 */
import { useLocation } from 'react-router-dom'
import { SITE_URL } from '../../data/seo'
import { serviceSlugs } from '../../data/servicePages'
import { cityPages } from '../../data/cityPages'

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'DigiSutra',
  url: SITE_URL,
  logo: `${SITE_URL}/logo-light.png`,
  description: 'Best digital marketing agency in Surat. B2B, real estate & marble sellers. Lead generation, Meta Ads, SEO, social media.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '812-814, Western Business Park, Opp. SD Jain School, Vesu',
    addressLocality: 'Surat',
    addressRegion: 'Gujarat',
    postalCode: '395007',
    addressCountry: 'IN',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+91-8200670752',
    email: 'info@digisutra.in',
    contactType: 'customer service',
    areaServed: 'IN',
    availableLanguage: 'English, Hindi',
  },
  sameAs: [
    'https://www.instagram.com/digisutraindia/',
    'https://www.facebook.com/digisutraindia/',
    'https://www.linkedin.com/company/digisutraindia',
  ],
}

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#organization`,
  name: 'DigiSutra',
  image: `${SITE_URL}/logo-light.png`,
  url: SITE_URL,
  telephone: '+91-8200670752',
  email: 'info@digisutra.in',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '812-814, Western Business Park, Opp. SD Jain School, Vesu',
    addressLocality: 'Surat',
    addressRegion: 'Gujarat',
    postalCode: '395007',
    addressCountry: 'IN',
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    opens: '10:30',
    closes: '18:30',
  },
  description: '360 digital marketing agency in Surat. Lead generation, Meta Ads, SEO, social media for B2B, real estate and marble sellers.',
}

const professionalServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#service`,
  name: 'DigiSutra Digital Marketing Agency',
  url: SITE_URL,
  telephone: '+91-8200670752',
  email: 'info@digisutra.in',
  description:
    'Best digital marketing agency in Surat specializing in B2B, real estate, and marble sellers. Services: Social Media Marketing, Meta Ads, SEO, Lead Generation, Funnel Building, Website Development.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '812-814, Western Business Park, Opp. SD Jain School, Vesu',
    addressLocality: 'Surat',
    addressRegion: 'Gujarat',
    postalCode: '395007',
    addressCountry: 'IN',
  },
  areaServed: [
    { '@type': 'City', name: 'Surat' },
    { '@type': 'City', name: 'Mumbai' },
    { '@type': 'City', name: 'Navi Mumbai' },
    { '@type': 'City', name: 'Bangalore' },
    { '@type': 'City', name: 'Hyderabad' },
    { '@type': 'City', name: 'Ahmedabad' },
    { '@type': 'City', name: 'Gandhinagar' },
    { '@type': 'City', name: 'Vadodara' },
    { '@type': 'City', name: 'Silvassa' },
    { '@type': 'City', name: 'Vapi' },
    { '@type': 'City', name: 'Valsad' },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Digital Marketing Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Social Media Marketing', url: `${SITE_URL}/services/social-media-marketing` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Meta Ads & Paid Advertising', url: `${SITE_URL}/services/paid-ads` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'SEO Services', url: `${SITE_URL}/services/seo` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Lead Generation', url: `${SITE_URL}/services/lead-generation` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Funnel Building', url: `${SITE_URL}/services/funnel-building` } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Website Development', url: `${SITE_URL}/services/website-development` } },
    ],
  },
}

function getBreadcrumbItems(pathname: string): { name: string; item: string }[] {
  const items: { name: string; item: string }[] = [{ name: 'Home', item: SITE_URL }]
  const path = pathname.replace(/\/$/, '') || '/'
  if (path === '/') return items
  const segments = path.split('/').filter(Boolean)
  let acc = ''
  for (const seg of segments) {
    acc += `/${seg}`
    const label =
      seg === 'about'
        ? 'About'
        : seg === 'contact'
          ? 'Contact'
          : seg === 'services'
            ? 'Services'
            : seg === 'insights'
              ? 'Knowledge & Insights'
              : seg === 'case-study'
                ? 'Case Studies'
                : seg === 'privacy'
                  ? 'Privacy'
                  : seg === 'terms'
                    ? 'Terms'
                    : seg
    const name = seg === 'services' && segments.length === 1 ? 'All Services' : seg.includes('-') ? seg.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : label
    items.push({ name, item: `${SITE_URL}${acc}` })
  }
  if (path.startsWith('/services/') && segments.length === 2) {
    const slug = segments[1]
    const service = serviceSlugs.find((s) => s.slug === slug)
    if (service) items[items.length - 1].name = service.title
  }
  if (segments.length === 1 && segments[0].startsWith('digital-marketing-agency-')) {
    const city = cityPages.find((c) => c.slug === segments[0])
    if (city) items[items.length - 1].name = `Digital Marketing Agency in ${city.cityName}`
  }
  return items
}

function BreadcrumbSchema() {
  const location = useLocation()
  const items = getBreadcrumbItems(location.pathname)
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.item,
    })),
  }
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  )
}

const contactFAQs = [
  { question: 'What industries do you specialise in?', answer: 'We specialise in working with high-value businesses across various sectors including real estate, marble sellers, machine manufacturers, and other businesses operating in the B2B segment.' },
  { question: 'How long does it take to see results?', answer: 'While initial campaign data can be seen within the first few weeks, we typically advise our clients to expect structured, predictable growth systems to show significant ROI within 60 to 90 days.' },
  { question: 'Do you offer custom packages?', answer: 'Yes. We don\'t believe in one-size-fits-all. Every business has unique challenges, so we tailor our BLS Growth Method to fit your specific goals and market dynamics.' },
  { question: 'What is the minimum budget required?', answer: 'Our strategies are built for scale. We recommend a minimum ad spend budget that allows for proper testing and optimization, which we can discuss during our initial strategy call based on your industry.' },
]

function FAQSchema() {
  const location = useLocation()
  if (location.pathname !== '/contact') return null
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: contactFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  }
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  )
}

export function JsonLd() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(professionalServiceSchema) }}
      />
      <BreadcrumbSchema />
      <FAQSchema />
    </>
  )
}
